import React, { memo, useEffect, useCallback, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { createSelector } from 'reselect';
import Swiper from 'react-id-swiper';
import { useInView } from 'react-intersection-observer';
import 'swiper/css/swiper.css';

import styles from './GameBanner.module.less';
import _bannerLink from '../../components/_bannerLink/_bannerLink';
import Button from '../../components/Button/Button';
import { logging, logPostInfo } from '../../actions/log';
import { CONTEXT } from '../../config';
import {
  loggingADX,
  logNative,
  getUrlByTargetContent,
  removeClass,
  addClass,
  changeClass,
} from '../../utils/utils';
import { setIframeVisible } from '../../actions/layout';
import { education } from '../../constants/storageKeys';
import { add } from 'lodash-es';
import Toast from '../../components/Toast/Toast';

const selectLogInfo = createSelector(
  state => state.log,
  log => log.logInfo,
);

let g_game_banner_active = null;
export default memo(({ data, logInfo }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const logInfoData = useSelector(selectLogInfo);

  const [swiperEl, setSwiperEl] = useState(null);
  const [bSmall, setBSmall] = useState(null);
  const [banners, setBanners] = useState(null);
  const [swiperParams, setSwiperParams] = useState(null);

  const maxBannerNum = 4;

  const [refInView, inView] = useInView({
    threshold: 0,
  });

  const LOG_ACTION_SOURCE = {
    c_action: `${logInfo.clTag || logInfo.tag}_Banner`,
    c_action_btn: `${logInfo.clTag || logInfo.tag}_Banner_btn`,
    c_action_adx: `${logInfo.clTag || logInfo.tag}_Banner_ADX`,
    p_adx: `${logInfo.clTag || logInfo.tag}_Banner_ADX`,
    source: `${logInfo.tag}_Banner`,
  };

  const onBannerChange = useCallback(bannerInfo => {
    let targetContent_ = {};
    try {
      targetContent_ = JSON.parse(bannerInfo?.targetContent || '{}');
    } catch (error) {}
    logInfo.changView({
      pageCardId: bannerInfo?.pageCardContentId,
      pageCardTitle: data && data.title,
      rankCode: data && data.dataList && data.dataList[0] && data.dataList[0].code,
      cardType: data && data.cardType,
      type: bannerInfo?.targetType,
      id: { ...targetContent_ },
    });
  }, []);

  // 筛洗数据，去除运营配置数据错误情况
  useEffect(() => {
    if (data && data?.dataList && inView) {
      const showBanners = [];
      let hasADXBanner = null;
      data.dataList.forEach(banner => {
        if (
          (banner.targetType === '1' && banner.appInfo) ||
          (banner.targetType === '2' && (banner.targetUrl || banner.targetContent)) ||
          (banner.targetType === '3' && banner.ztId) ||
          (banner.targetType === '5' && banner.targetContent) ||
          (banner.targetType === '11' && banner.targetContent) ||
          (banner.targetType === '12' && banner.targetContent)
        ) {
          showBanners.push(banner);
        }
        // 是否含有adx数据
        if (banner.targetType === '10' && banner.advertCrid && banner.advertUrl) {
          showBanners.push(banner);
          hasADXBanner = banner;
        }
      });
      setBanners(showBanners.slice(0, maxBannerNum));
      // 含有adx数据，则打展示的桩
      if (hasADXBanner) {
        loggingADXPV(hasADXBanner);
      }
    } else {
      setBanners(null);
    }
  }, [data, inView]);

  // 根据banners && swiperEl && bSmall 同事存在的时候设置swiper参数和
  useEffect(() => {
    if (banners && swiperEl && bSmall) {
      getSwiperParams(banners);
    } else {
      setSwiperParams(null);
    }
  }, [banners, swiperEl, bSmall]);

  const loggingADXPV = useCallback(bannerADX => {
    // 推荐页面ADX banner展示打桩
    dispatch(
      logging({
        category: 'I',
        action: LOG_ACTION_SOURCE.p_adx,
        label: '',
        param2: {
          source: `${LOG_ACTION_SOURCE.source}||${bannerADX.pageCardContentId}`,
          source_p: logInfoData.source_p,
          source_pp: logInfoData.source_pp,
          advertCrid: bannerADX.advertCrid,
          advertUrl: bannerADX.advertUrl,
          age_period: logInfo.tag === 'Edu' ? localStorage.getItem(education.EDUSTAGE) : '', // 教育频道带学段信息
        },
        param3: bannerADX.bizInfo,
      }),
    );
    if (bannerADX && bannerADX.impTracks) {
      const impTracks = JSON.parse(bannerADX.impTracks);
      loggingADX(impTracks);
    }
  }, []);

  const loggingADXClick = useCallback(bannerADX => {
    // 推荐页面ADX banner点击打桩
    dispatch(
      logging({
        category: 'C',
        action: LOG_ACTION_SOURCE.c_action_adx,
        label: '',
        param2: {
          source: `${LOG_ACTION_SOURCE.source}||${bannerADX.pageCardContentId}`,
          source_p: logInfoData.source_p,
          source_pp: logInfoData.source_pp,
          advertCrid: bannerADX.advertCrid,
          advertUrl: bannerADX.advertUrl,
          age_period: logInfo.tag === 'Edu' ? localStorage.getItem(education.EDUSTAGE) : '', // 教育频道带学段信息
        },
        param3: bannerADX.bizInfo,
      }),
    );
    if (bannerADX && bannerADX.impTracks) {
      const clickTracks = JSON.parse(bannerADX.clickTracks);
      loggingADX(clickTracks);
    }
  }, []);

  const handleBannerBtn = useCallback(
    bannerInfo => {
      let targetContent_ = {};
      try {
        targetContent_ = JSON.parse(bannerInfo?.targetContent || '{}');
      } catch (error) {}
      // 推荐页面banner btn点击打桩
      dispatch(
        logging({
          category: 'C',
          action: LOG_ACTION_SOURCE.c_action_btn,
          label: '',
          param2: {
            source: `${LOG_ACTION_SOURCE.source}||${bannerInfo.pageCardContentId}`,
            source_p: logInfoData.source_p,
            source_pp: logInfoData.source_pp,
            type: bannerInfo?.targetType,
            id: { ...targetContent_ },
          },
          param3: bannerInfo.bizInfo,
        }),
      );
    },
    [dispatch, logInfoData],
  );

  const handleBanner = useCallback(
    bannerInfo => {
      let targetContent_ = {};
      try {
        targetContent_ = JSON.parse(bannerInfo?.targetContent || '{}');
      } catch (error) {}
      // 推荐页面banner点击打桩
      dispatch(
        logging({
          category: 'C',
          action: LOG_ACTION_SOURCE.c_action,
          label: '',
          param2: {
            source: `${LOG_ACTION_SOURCE.source}||${bannerInfo.pageCardContentId}`,
            source_p: logInfoData.source_p,
            source_pp: logInfoData.source_pp,
            type: bannerInfo?.targetType,
            id: { ...targetContent_ },
          },
          param3: bannerInfo.bizInfo,
        }),
      );

      setTimeout(() => {
        if (
          bannerInfo.targetType === '1' ||
          bannerInfo.targetType === '3' ||
          bannerInfo.targetType === '11' ||
          bannerInfo.targetType === '12'
        ) {
          dispatch(
            logPostInfo({
              source_p: `${LOG_ACTION_SOURCE.source}||${bannerInfo.pageCardContentId}`,
              source_pp: logInfoData.source_p,
            }),
          );
          let url = `${CONTEXT}/`;
          if (bannerInfo.targetType && bannerInfo.targetContent) {
            url = getUrlByTargetContent(bannerInfo);
          }
          history.push(url);
        } else if (bannerInfo.targetType === '2') {
          if (bannerInfo.targetUrl) {
            window.open(bannerInfo.targetUrl);
          } else if (bannerInfo.targetContent) {
            try {
              const targetContent = JSON.parse(bannerInfo.targetContent) || {};
              window.open(targetContent.targetUrl);
            } catch (error) {}
          }
        } else if (bannerInfo.targetType === '5' && bannerInfo.targetContent) {
          let targetContent = {};
          try {
            targetContent = JSON.parse(bannerInfo.targetContent);
          } catch (error) {
            logNative(`分析商城URL JSON解析错误:${error}`, error);
          }
          if (targetContent && targetContent.url) {
            dispatch(
              setIframeVisible({
                visible: true,
                url: targetContent.url,
                source: LOG_ACTION_SOURCE.source,
              }),
            );
          } else {
            Toast.error({ content: '数据错误！' });
          }
        } else if (
          bannerInfo.targetType === '10' &&
          bannerInfo.advertCrid &&
          bannerInfo.advertUrl
        ) {
          loggingADXClick(bannerInfo);
          window.open(bannerInfo.advertUrl);
        } else {
          history.push(`${CONTEXT}/error`);
        }
      }, 30);
    },
    [dispatch, logInfoData, history],
  );

  const onMouseOver = useCallback(e => {
    const el = e.currentTarget.getElementsByClassName('banner_img')[0];
    addClass(el, styles.image_mouse_in);
  }, []);

  // 小banner 上的动画实现
  const changeSmallBanner = useCallback(
    (activeIndex, maxIndex) => {
      // const prevIndex = activeIndex === 1 ? maxIndex : activeIndex - 1;
      // const nextIndex = swiperObj.activeIndex === maxIndex ? 1 : swiperObj.activeIndex;
      try {
        if (bSmall) {
          const bgDoms = bSmall?.getElementsByClassName(`banner_small_item_bg`);
          const LineDoms = bSmall?.getElementsByClassName(`banner_small_item_line`);
          [...bgDoms].map((item, i) => {
            item && removeClass(item, styles.s_banner_show);
          });
          [...LineDoms].map((item, i) => {
            item && removeClass(item, styles.progress_bar);
          });

          const activeDom = bSmall?.getElementsByClassName(`b_s_bg${activeIndex}`)[0];
          const activeDomLine = bSmall?.getElementsByClassName(`b_s_line${activeIndex}`)[0];
          if (activeDom && activeDomLine) {
            addClass(activeDom, styles.s_banner_show);
            addClass(activeDomLine, styles.progress_bar);
          }
        }
      } catch (error) {}
    },
    [bSmall],
  );

  const getSwiperParams = useCallback(
    bannersArr => {
      const params = {
        centeredSlides: true,
        slidesPerView: '1',
        spaceBetween: 0,
        speed: 500,
        effect: 'fade',
        loop: true,
        autoplay: {
          delay: 4000,
          stopOnLastSlide: false,
          disableOnInteraction: false,
        },
        on: {
          click(e) {
            if (swiperEl && swiperEl && swiperEl.swiper) {
              const swiperObj = swiperEl.swiper;
            }
          },
          slideChangeTransitionEnd(e) {
            if (swiperEl && swiperEl && swiperEl.swiper) {
              const swiperObj = swiperEl.swiper;
            }
          },
          slideChangeTransitionStart(e) {
            // 不知道为啥点击banner后多次触发此函数，跳转走了也会上报，所以直接通过location过滤处理
            if (window?.location?.href?.indexOf('game/') === -1) {
              return;
            }
            // 当鼠标移走，切换动画开始的时候删除之前banner的动画class
            if (swiperEl && swiperEl && swiperEl.swiper) {
              const swiperObj = swiperEl.swiper;
              const maxIndex = bannersArr.length;
              const activeIndex = swiperObj.activeIndex;

              if (activeIndex === maxIndex + 1) {
                swiperObj.slideTo(1);
                return;
              }

              // 小banner 上的动画实现
              changeSmallBanner(activeIndex, maxIndex);

              // 取消上一个banner的放大效果
              if (swiperObj && swiperObj.slides && swiperObj.slides.length > 0) {
                const dom = swiperObj.slides[activeIndex];
                const el = dom.getElementsByClassName('banner_img')[0];
                removeClass(el, styles.image_mouse_in);
              }
              // 打桩
              if (g_game_banner_active !== activeIndex && bannersArr[activeIndex - 1]) {
                g_game_banner_active = activeIndex;
                onBannerChange(bannersArr[activeIndex - 1]);
              }
            }
          },
        },
      };
      setSwiperParams(params);
    },
    [swiperEl, bSmall, history],
  );

  const formatPriceType3 = useCallback((num = '0') => {
    return parseFloat((num || '0')?.replace('¥', '').replace('￥', ''));
  }, []);
  const formatPrice = useCallback((num = '0') => {
    return `${parseFloat((parseInt(num) / 100).toFixed(2)) || 0}`;
  }, []);
  const formatPriceZK = useCallback((cu_, or_) => {
    let cu = parseFloat(cu_);
    let or = parseFloat(or_);
    if (cu && or) {
      return `${parseFloat((cu / or).toFixed(2)) * 10 || 0}`;
    }
    return '';
  }, []);

  const handleText = e => {
    if (e?.appInfo.appBizType === 0 || e?.appInfo.appBizType === 1) {
      if (e?.appInfo?.androidType === '3') {
        if (
          e?.appInfo?.sourceInfo &&
          e?.appInfo?.sourceInfo?.sellInfo &&
          (e?.appInfo?.sourceInfo?.sellInfo?.originalPrice ||
            e?.appInfo?.sourceInfo?.sellInfo?.currentPrice)
        ) {
          return '立即购买';
        } else {
          return '立即下载';
        }
      } else {
        return '立即安装';
      }
    } else {
      if (e?.appInfo?.appBizContent) {
        return '立即预约';
      } else {
        if (e?.appInfo?.androidType === '3') {
          if (
            e?.appInfo?.sourceInfo &&
            e?.appInfo?.sourceInfo?.sellInfo &&
            (e?.appInfo?.sourceInfo?.sellInfo?.originalPrice ||
              e?.appInfo?.sourceInfo?.sellInfo?.currentPrice)
          ) {
            return '立即购买';
          } else {
            return '立即下载';
          }
        } else {
          return '立即安装';
        }
      }
    }
  };
  return (
    <div
      className={`${styles.card}  ${inView ? styles.card_cardIn : styles.card_cardOut}`}
      ref={refInView}
      data-item={data.cardType}
    >
      {banners && banners.length > 0 && (
        <>
          <Swiper {...swiperParams} ref={r => setSwiperEl(r)} rebuildOnUpdate>
            {banners.map((item, i) => (
              <div
                className={`banner_item`}
                key={i}
                onClick={e => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleBanner(item);
                }}
                onMouseEnter={() => {
                  if (swiperEl.swiper) {
                    swiperEl.swiper.autoplay.stop();
                  }
                }}
                onMouseLeave={() => {
                  if (swiperEl.swiper) {
                    swiperEl.swiper.autoplay.start();
                  }
                }}
                onMouseOver={e => onMouseOver(e)}
              >
                {/* <img className={`banner_img`} src={item.contentImgBig || item.contentImg} alt="" /> */}
                <div
                  className={`banner_img`}
                  style={{ backgroundImage: `url('${item.contentImgBig || item.contentImg}')` }}
                ></div>
                <div className={`banner_img_bg`}></div>
                <div className={`banner_info`}>
                  <div className={`banner_info_name`}>{item?.contentTitle}</div>
                  <div className={`banner_info_desc`}>{item?.contentDesc}</div>

                  {((item?.relationTags && item?.relationTags?.length > 0) ||
                    (item?.relationRanks && item?.relationRanks?.length > 0)) && (
                    <div className={`banner_info_tags`}>
                      {item?.relationTags && item?.relationTags[0] && (
                        <div className={`banner_info_tag1`}>
                          <_bannerLink
                            cardData={{ ...item?.relationTags[0] }}
                            logInfo={{
                              ...logInfo,
                              source: `${logInfo.tag}_Banner`,
                              action: `${logInfo.clTag || logInfo.tag}_Banner_Rank`,
                              // rankid: item?.relationRanks[0].name || '',
                            }}
                          >
                            {item?.relationTags[0].name}
                          </_bannerLink>
                        </div>
                      )}
                      {item?.relationRanks && item?.relationRanks[0] && (
                        <div className={`banner_info_tag2`}>
                          <_bannerLink
                            cardData={{ ...item?.relationRanks[0] }}
                            logInfo={{
                              ...logInfo,
                              source: `${logInfo.tag}_Banner`,
                              action: `${logInfo.clTag || logInfo.tag}_Banner_Rank`,
                              // rankid: item?.relationRanks[0].name || '',
                            }}
                          >
                            {item?.relationRanks[0].name}
                          </_bannerLink>
                        </div>
                      )}
                    </div>
                  )}

                  {item?.appInfo?.androidType === '3' &&
                    item?.appInfo?.sourceInfo &&
                    item?.appInfo?.sourceInfo?.sellInfo &&
                    (item?.appInfo?.sourceInfo?.sellInfo?.currentPrice ||
                      item?.appInfo?.sourceInfo?.sellInfo?.originalPrice) && (
                      <div className={`banner_info_price`}>
                        {item?.appInfo?.sourceInfo?.sellInfo?.discountDesc ? (
                          <div className={`soft_price_zk`}>
                            {item?.appInfo?.sourceInfo?.sellInfo?.discountDesc}
                          </div>
                        ) : (
                          item?.appInfo?.sourceInfo?.sellInfo?.discount && (
                            <div className={`soft_price_zk`}>
                              {item?.appInfo?.sourceInfo?.sellInfo?.discount} 折
                            </div>
                          )
                        )}

                        {((item?.appInfo?.sourceInfo?.sellInfo?.currentPrice &&
                          !item?.appInfo?.sourceInfo?.sellInfo?.originalPrice) ||
                          (!item?.appInfo?.sourceInfo?.sellInfo?.currentPrice &&
                            item?.appInfo?.sourceInfo?.sellInfo?.originalPrice) ||
                          item?.appInfo?.sourceInfo?.sellInfo?.currentPrice ===
                            item?.appInfo?.sourceInfo?.sellInfo?.originalPrice) && (
                          <div className={`soft_price_c`}>
                            ¥
                            {formatPriceType3(
                              item?.appInfo?.sourceInfo?.sellInfo?.currentPrice ||
                                item?.appInfo?.sourceInfo?.sellInfo?.originalPrice,
                            )}
                          </div>
                        )}
                        {item?.appInfo?.sourceInfo?.sellInfo?.currentPrice &&
                          item?.appInfo?.sourceInfo?.sellInfo?.originalPrice &&
                          item?.appInfo?.sourceInfo?.sellInfo?.currentPrice !==
                            item?.appInfo?.sourceInfo?.sellInfo?.originalPrice && (
                            <>
                              <div className={`soft_price_c`}>
                                ¥
                                {formatPriceType3(
                                  item?.appInfo?.sourceInfo?.sellInfo?.currentPrice,
                                )}
                              </div>
                              <div className={`soft_price_o`}>
                                ¥
                                {formatPriceType3(
                                  item?.appInfo?.sourceInfo?.sellInfo?.originalPrice,
                                )}
                              </div>
                            </>
                          )}
                      </div>
                    )}

                  {item?.appInfo?.payType === 1 && (
                    <div className={`banner_info_price`}>
                      {item?.appInfo?.currentPrice &&
                        item?.appInfo?.originalPrice &&
                        item?.appInfo?.currentPrice !== item?.appInfo?.originalPrice && (
                          <div className={`soft_price_zk`}>
                            {formatPriceZK(
                              item?.appInfo?.currentPrice,
                              item?.appInfo?.originalPrice,
                            )}{' '}
                            折
                          </div>
                        )}
                      {((!item?.appInfo?.currentPrice && item?.appInfo?.originalPrice) ||
                        (item?.appInfo?.currentPrice && !item?.appInfo?.originalPrice) ||
                        item?.appInfo?.currentPrice === item?.appInfo?.originalPrice) && (
                        <div className={`soft_price_c`}>
                          ¥
                          {formatPrice(item?.appInfo?.currentPrice || item?.appInfo?.originalPrice)}
                        </div>
                      )}
                      {item?.appInfo?.currentPrice &&
                        item?.appInfo?.originalPrice &&
                        item?.appInfo?.currentPrice !== item?.appInfo?.originalPrice && (
                          <>
                            <div className={`soft_price_c`}>
                              ¥{formatPrice(item?.appInfo?.currentPrice)}
                            </div>
                            <div className={`soft_price_o`}>
                              ¥{formatPrice(item?.appInfo?.originalPrice)}
                            </div>
                          </>
                        )}
                    </div>
                  )}

                  <div
                    className={`banner_info_opt`}
                    onClick={e => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleBannerBtn(item);
                    }}
                    onMouseEnter={() => {
                      if (swiperEl.swiper) {
                        swiperEl.swiper.autoplay.stop();
                      }
                    }}
                    onMouseLeave={() => {
                      if (swiperEl.swiper) {
                        swiperEl.swiper.autoplay.start();
                      }
                    }}
                  >
                    {item.targetType === '5' && (
                      <div
                        className={styles.banner_shop_btn}
                        onClick={e => {
                          e.stopPropagation();
                          handleBanner(item);
                        }}
                      >
                        立即购买
                      </div>
                    )}
                    {item.targetType !== '5' && !(item?.targetType === '1' && item?.appInfo) && (
                      <div
                        className={styles.banner_shop_btn}
                        onClick={e => {
                          e.stopPropagation();
                          handleBanner(item);
                        }}
                      >
                        查看
                      </div>
                    )}
                    {item?.targetType === '1' && item?.appInfo && (
                      <Button
                        soft={item?.appInfo}
                        info={{
                          btnStyle: 'opacity',
                          text: handleText(item),
                          className: styles.soft_button,
                          className_busing: styles.soft_button_busing,
                          className_open: styles.soft_button_open,
                        }}
                        logInfo={{
                          source: `${LOG_ACTION_SOURCE.source}||${item?.pageCardContentId}`,
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </Swiper>

          <div className={`banner_small`} ref={r => setBSmall(r)}>
            {banners.map((item, i) => (
              <div
                className={`banner_small_item`}
                key={i}
                onClick={e => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleBanner(item);
                }}
                onMouseEnter={e => {
                  if (swiperEl.swiper) {
                    swiperEl.swiper.slideTo(i + 1);
                    swiperEl.swiper.autoplay.stop();
                  }
                }}
                onMouseLeave={() => {
                  if (swiperEl.swiper) {
                    swiperEl.swiper.autoplay.start();
                  }
                }}
              >
                <img
                  className={`banner_small_img`}
                  src={item.contentImgBig || item.contentImg}
                  alt=""
                />
                <div className={`banner_small_item_bg ${'b_s_bg' + (i + 1)}`}></div>
                <div className={`banner_small_item_line ${'b_s_line' + (i + 1)}`}></div>
                <div className={`banner_small_item_name`}>{item?.contentTitle}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
});
